/**
 * Orgus Watch in the top bar (ADR 0058 decision 3): every incident nobody has taken yet is a
 * notice, so the notice count includes the open incident count, and a watch that stopped
 * checking is a notice too. Mounted once by the shell; renders nothing itself.
 */
import { useEffect, useMemo } from 'react';
import { WATCH_TICK_MS } from '@orgus/protocol';
import { useNow } from '@orgus/ui';
import { isMockStill } from '../../lib/mock.ts';
import { useOrgus } from '../../lib/store.ts';
import { publishNotice, useNoticeStore, withdrawNotice } from '../../components/shell/notices.ts';
import { incidentNotices, staleNotice, watchStateOf } from './model.ts';

/** Demo mode: the watch ticks with the demo, so "Last checked" stays fresh (held still under ?still=1). */
function useMockTick(): void {
  const mock = useOrgus((s) => s.mode === 'mock');
  const now = useNow(WATCH_TICK_MS);
  useEffect(() => {
    if (!mock || isMockStill()) return;
    const st = useOrgus.getState();
    const w = watchStateOf(st.ext);
    if (w && w.lastTickAt != null && now - w.lastTickAt >= WATCH_TICK_MS) st.applyExt({ ext: 'watch', type: 'state', data: { ...w, lastTickAt: now } });
  }, [mock, now]);
}

export function WatchNotices() {
  useMockTick();
  const raw = useOrgus((s) => s.ext.watch);
  const now = useNow(10_000);
  const state = useMemo(() => watchStateOf({ watch: raw }), [raw]);
  // The incident notices follow the slice; the stale notice also the clock.
  const incidents = useMemo(() => incidentNotices(state), [state]);
  const stale = staleNotice(state, now);
  const staleKey = stale ? stale.title : null;
  const notices = useMemo(() => (stale ? [stale, ...incidents] : incidents), [incidents, staleKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const ids = new Set(notices.map((n) => n.id));
    for (const n of notices) publishNotice(n);
    for (const id of Object.keys(useNoticeStore.getState().byId)) if (id.startsWith('watch.') && !ids.has(id)) withdrawNotice(id);
  }, [notices]);
  useEffect(
    () => () => {
      for (const id of Object.keys(useNoticeStore.getState().byId)) if (id.startsWith('watch.')) withdrawNotice(id);
    },
    [],
  );
  return null;
}
